import React, { useState, useEffect } from 'react';
import { withRouter } from 'react-router-dom';
import ReactLoading from 'react-loading';
import { toast } from '@rickylandino/react-messages';
import { medicalHistoryService } from '../../common/services/PatientMedicalHistoryService';
import FamilyHistoryCard from './FamilyHistoryCard';
import PatientBreastHistoryCard from './PatientBreastHistoryCard';
import PatientBreastCurrentCard from './PatientBreastCurrentCard';
import PatientMedicalDetailsCard from './PatientMedicalDetailsCard';
import '../../css/card.css';

const RESPONSE_VALUES = {
    NO: 'No',
    YES: 'Yes',
    UNKNOWN: "I don't know",
    NA: 'N/A'
};

const LATERALITY_OPTIONS = [
    { value: '', label: 'Select...' },
    { value: 'Left', label: 'Left' },
    { value: 'Right', label: 'Right' },
    { value: 'Bilateral', label: 'Bilateral' }
];

// List of all possible history items
const ALL_HISTORY_ITEMS = [
    // Family History fields
    'FamilyHistoryOfBreastCancer', 'MotherHadBreastCancer', 'SisterHadBreastCancer',
    'DaughterHadBreastCancer', 'OtherHadBreastCancer',
    
    // Patient Breast History fields
    'HaveYouHadBreastCancer', 'Chemotherapy', 'RadiationTherapy', 'HaveYouHadBreastSurgery',
    
    // Surgery fields
    'CystAspiration', 'SurgicalBiopsy', 'NeedleBiopsy', 'Mastectomy', 'Lumpectomy',
    'Implants', 'Reconstruction', 'Reduction', 'OtherBreastSurgery',
    
    // Mammogram
    'PreviousMammogram',

    // Additional fields
    'OtherBreastProblems', 'PatientPregnant', 'CaucausianWhite', 'HispanicLatina',
    'Menopause', 'GeneticMutation', 'Weight', 'BreastSymptoms', 'OtherBreastMarkings',
    'GivenBirth', 'NativeHawaiianOtherPacificIslander', 'AgeatFirstMenstrualPeriod',
    'AmericanIndianAlaskaNative', 'DateofLastMenstrualPeriod', 'Scars', 'OtherRace',
    'BreastPain', 'NippleDischarge', 'Asian', 'Hormones', 'Height', 'AJAncestry',
    'NippleRetraction', 'FocalPain', 'PatientHasBreastProblems', 'OtherTypesofCancer',
    'Bruises', 'LumpsThickness', 'AfricanAmericanBlack', 'PatientHasBreastMarkings',
    'FamilyHadBreastCancer', 'OralContraceptives', 'Moles'
];

// Create empty database record
const createEmptyRecord = (historyItem, value = '') => ({
    historyItem,
    value,
    laterality: '',
    date: '',
    ageAtDiagnosis: '',
    typeOfCancer: '',
    facility: '',
    focalPainYN: '',
    clearBloody: '',
    newYN: '',
    flagYN: 0,
    autoFlagYN: 0,
    inactiveYN: 0,
    resetYearlyYN: 0,
    typeOfHRT: '',
    lengthOfHRTUse: '',
    timeSinceHRTUse: '',
    intendedLengthOfHRTUse: '',
    notes: ''
});

const ConsolidatedFamilyHistoryTab = ({ initialData, disabled = false }) => {
    // Store as object keyed by historyItem for easy access in UI
    const [medicalHistory, setMedicalHistory] = useState({});
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const patientId = initialData?.patientID || new URLSearchParams(window.location.search).get('patientID');

    // Load existing data - comes as array from API
    useEffect(() => {
        const loadData = async () => {
            if (!patientId) return;

            setLoading(true);
            try {
                const dataArray = await medicalHistoryService.getMedicalHistory(patientId);
                console.log('Loaded medical history array:', dataArray);
                
                // Convert array to object keyed by historyItem for easy UI access
                const dataObject = {};
                
                // Initialize all history items with empty records
                ALL_HISTORY_ITEMS.forEach(historyItem => {
                    dataObject[historyItem] = createEmptyRecord(historyItem);
                });
                
                // Overlay loaded data
                if (Array.isArray(dataArray)) {
                    dataArray.forEach(record => {
                        if (record.historyItem) {
                            dataObject[record.historyItem] = record;
                        }
                    });
                }
                
                setMedicalHistory(dataObject);
            } catch (error) {
                console.error('Error loading medical history:', error);
                toast.error('Failed to load medical history');
                
                // Initialize with empty records on error
                const emptyData = {};
                ALL_HISTORY_ITEMS.forEach(historyItem => {
                    emptyData[historyItem] = createEmptyRecord(historyItem);
                });
                setMedicalHistory(emptyData);
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, [patientId]);

    // Save data - convert object back to array for API
    const handleSave = async () => {
        if (!patientId) {
            toast.error('Patient ID is required');
            return;
        }

        setSaving(true);
        try {
            // Convert object to array for API (only include records with data)
            const dataArray = Object.values(medicalHistory).filter(record => {
                // Include if value is not empty or if any other meaningful field has data
                return record.value || 
                       record.laterality || 
                       record.date || 
                       record.ageAtDiagnosis || 
                       record.typeOfCancer || 
                       record.facility || 
                       record.notes ||
                       record.flagYN > 0 ||
                       record.autoFlagYN > 0;
            });
            
            console.log('Saving medical history array:', dataArray);
            await medicalHistoryService.updateMedicalHistory(patientId, dataArray);
            toast.success('Medical history saved successfully');
        } catch (error) {
            console.error('Error saving medical history:', error);
            toast.error('Failed to save medical history');
        } finally {
            setSaving(false);
        }
    };

    // Handle field changes
    const handleFieldChange = (historyItem, updates) => {
        console.log(`Updating ${historyItem}:`, updates);
        setMedicalHistory(prev => ({
            ...prev,
            [historyItem]: { ...prev[historyItem], ...updates }
        }));
    };

    // Handle multiple field changes (like clearing related fields)
    const handleMultipleFieldChanges = (updates) => {
        console.log('Multiple field updates:', updates);
        setMedicalHistory(prev => {
            const newState = { ...prev };
            Object.keys(updates).forEach(historyItem => {
                newState[historyItem] = { ...prev[historyItem], ...updates[historyItem] };
            });
            return newState;
        });
    };

    if (loading) {
        return (
            <div className="text-center p-4">
                <ReactLoading type="spokes" color="#F30079" height={50} width={50} />
                <p className="mt-3">Loading medical history...</p>
            </div>
        );
    }

    return (
        <div className="consolidated-family-history">
            <FamilyHistoryCard
                medicalHistory={medicalHistory}
                onFieldChange={handleFieldChange}
                onMultipleFieldChanges={handleMultipleFieldChanges}
                disabled={disabled}
            />

            <PatientBreastHistoryCard
                medicalHistory={medicalHistory}
                onFieldChange={handleFieldChange}
                onMultipleFieldChanges={handleMultipleFieldChanges}
                disabled={disabled}
            />

            <PatientBreastCurrentCard
                medicalHistory={medicalHistory}
                onFieldChange={handleFieldChange}
                onMultipleFieldChanges={handleMultipleFieldChanges}
                disabled={disabled}
            />

            <PatientMedicalDetailsCard
                medicalHistory={medicalHistory}
                onFieldChange={handleFieldChange}
                disabled={disabled}
            />

            {!disabled && (
                <div className="text-center mt-3">
                    {saving ? (
                        <ReactLoading type="spokes" color="#F30079" height={30} width={30} />
                    ) : (
                        <button className="btn btn-submit" onClick={handleSave}>
                            Save Medical History
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

export default withRouter(ConsolidatedFamilyHistoryTab);
export { RESPONSE_VALUES, createEmptyRecord, ALL_HISTORY_ITEMS, LATERALITY_OPTIONS };